import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchBoxAccessToken } from "../../store/common/authSlice";
import StlViewer from "../../components/admin/StlViewer";

const StlViewerPage = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const [searchParams] = useSearchParams();
  const fileName = searchParams.get("name") || "model.stl";

  const dispatch = useAppDispatch();
  const { boxAccessToken } = useAppSelector((s) => s.auth);

  const [arrayBuffer, setArrayBuffer] = useState<ArrayBuffer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = `${fileName} · STL Viewer`;
  }, [fileName]);

  useEffect(() => {
    dispatch(fetchBoxAccessToken());
  }, [dispatch]);

  useEffect(() => {
    if (!fileId || !boxAccessToken) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setArrayBuffer(null);
      try {
        const res = await fetch(
          `https://api.box.com/2.0/files/${fileId}/content`,
          {
            headers: { Authorization: `Bearer ${boxAccessToken}` },
          }
        );
        if (!res.ok) throw new Error("Failed to load STL");
        const buffer = await res.arrayBuffer();
        if (!cancelled) {
          setArrayBuffer(buffer);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError("Failed to load STL file from Box.");
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [fileId, boxAccessToken]);

  const handleClose = () => {
    window.close();
    // Fallback if the browser blocks closing a non-script-opened tab
    setTimeout(() => {
      if (!window.closed) {
        if (window.history.length > 1) window.history.back();
        else window.location.href = "/dashboard";
      }
    }, 100);
  };

  return (
    <StlViewer
      fileName={fileName}
      arrayBuffer={arrayBuffer}
      loading={loading || (!boxAccessToken && !error)}
      error={error}
      onClose={handleClose}
    />
  );
};

export default StlViewerPage;
